import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { BottomNav } from "@/components/BottomNav";
import { useLanguage } from "@/contexts/LanguageContext";
import { Paywall } from "@/components/Paywall";
import { 
  Camera, Search, ArrowLeft, Upload, Loader2, X, Check,
  Home, Droplet, LineChart, MessageCircle, BarChart3,
  AlertTriangle, CheckCircle, Info, Plus, Scale, ChevronRight, FileText
} from "lucide-react";
import { toast } from "sonner";

// Portion size options
const PORTION_OPTIONS = [
  { label: "Small", grams: 80, description: "~80g" },
  { label: "Medium", grams: 150, description: "~150g" },
  { label: "Large", grams: 250, description: "~250g" },
  { label: "Custom", grams: null, description: "Enter grams" }
];

// Meal type options
const MEAL_TYPES = [
  { value: "breakfast", label: "Breakfast", carbTarget: "30-45g" },
  { value: "lunch", label: "Lunch", carbTarget: "45-60g" },
  { value: "dinner", label: "Dinner", carbTarget: "45-60g" },
  { value: "snack", label: "Snack", carbTarget: "15-20g" }
];

const EnhancedMealAnalysis = () => {
  const { token, user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // Flow states
  const [step, setStep] = useState("search"); // search, confirm, portion, result
  const [loading, setLoading] = useState(false);
  
  // Food selection
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Portion selection
  const [portions, setPortions] = useState({});
  const [mealType, setMealType] = useState("lunch");
  
  // Analysis result
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Paywall
  const [showPaywall, setShowPaywall] = useState(false);
  const [usageData, setUsageData] = useState(null);
  
  // Meal Plan Reminder state
  const [hasMealPlans, setHasMealPlans] = useState(null); // null = loading, true = has plans, false = no plans
  
  const searchInputRef = useRef(null);
  
  // Check if user should see meal plan reminder
  // Show for: B2C users without care team (including users with no explicit assignment)
  const shouldShowMealPlanReminder = user && (
    // B2C user type AND no care team (false, null, or undefined all mean no care team)
    user.user_type === "b2c" && !user.has_care_team
  );

  // Check if user has meal plans (for users who should see the reminder)
  useEffect(() => {
    const checkMealPlans = async () => {
      // Wait for user data to be loaded
      if (!user) {
        return; // Don't set anything yet, wait for user data
      }
      
      // Users who shouldn't see the reminder - hide banner
      if (!shouldShowMealPlanReminder) {
        setHasMealPlans(true); // Hide banner
        return;
      }
      
      // Check their plans
      try {
        const response = await fetch(`${API}/meal-plans/my-plans`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        if (response.ok) {
          const data = await response.json();
          const hasPlans = data.plans && data.plans.length > 0;
          setHasMealPlans(hasPlans);
        } else {
          setHasMealPlans(false);
        }
      } catch (error) {
        console.error("Check meal plans error:", error);
        setHasMealPlans(false);
      }
    };
    
    if (token && user) {
      checkMealPlans();
    }
  }, [token, user, shouldShowMealPlanReminder]);

  // Check usage limit on mount
  useEffect(() => {
    const checkUsage = async () => {
      try {
        const response = await fetch(`${API}/user/can-analyze-meal`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        if (response.ok) {
          const data = await response.json();
          setUsageData(data);
          if (!data.can_analyze) {
            setShowPaywall(true);
          }
        }
      } catch (error) {
        console.error("Usage check error:", error);
      }
    };
    checkUsage();
  }, [token]);

  // Search foods with debounce
  useEffect(() => {
    const searchFoods = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetch(`${API}/foods?search=${encodeURIComponent(searchQuery)}`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data);
        }
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchFoods, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, token]);

  const selectFood = (food) => {
    if (!selectedFoods.find(f => f.food_id === food.food_id)) {
      setSelectedFoods([...selectedFoods, food]);
      // Set default portion
      setPortions(prev => ({
        ...prev,
        [food.food_id]: food.typical_portion_g || 150
      }));
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const removeFood = (foodId) => {
    setSelectedFoods(selectedFoods.filter(f => f.food_id !== foodId));
    const newPortions = { ...portions };
    delete newPortions[foodId];
    setPortions(newPortions);
  };

  const updatePortion = (foodId, grams) => {
    setPortions(prev => ({
      ...prev,
      [foodId]: parseInt(grams) || 100
    }));
  };

  const proceedToConfirm = () => {
    if (selectedFoods.length === 0) {
      toast.error("Please select at least one food");
      return;
    }
    setStep("confirm");
  };

  const proceedToPortion = () => {
    setStep("portion");
  };

  const analyzeWithPortions = async () => {
    if (usageData && !usageData.can_analyze && usageData.reason !== "premium") {
      setShowPaywall(true);
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API}/meals/analyze-enhanced`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          food_ids: selectedFoods.map(f => f.food_id),
          portions: portions,
          meal_type: mealType
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysisResult(data);
        setStep("result");
        toast.success("Meal analyzed successfully!");
        
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        toast.error("Failed to analyze meal");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze meal");
    } finally {
      setLoading(false);
    }
  };

  const resetAnalysis = () => {
    setStep("search");
    setSelectedFoods([]);
    setPortions({});
    setAnalysisResult(null);
    setSearchQuery("");
  };

  const getCarbClassColor = (classification) => {
    switch (classification) {
      case "low": return "text-blue-600 bg-blue-50";
      case "ideal": return "text-success bg-success/10";
      case "high": return "text-warning bg-warning/10";
      default: return "text-muted-foreground bg-muted";
    }
  };

  const getGIColor = (impact) => {
    if (impact === "Low") return "text-success";
    if (impact === "Medium") return "text-warning";
    return "text-destructive";
  };

  // Render based on step
  const renderContent = () => {
    switch (step) {
      case "search":
        return renderSearchStep();
      case "confirm":
        return renderConfirmStep();
      case "portion":
        return renderPortionStep();
      case "result":
        return renderResultStep();
      default:
        return renderSearchStep();
    }
  };

  const renderSearchStep = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="font-heading text-2xl font-bold text-secondary">
          {t('meal_analysis.what_foods_in_meal')}
        </h1>
        <p className="text-muted-foreground">
          {t('meal_analysis.search_foods_desc')}
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          ref={searchInputRef}
          type="text"
          placeholder={t('meal_analysis.search_food_placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-12 rounded-2xl border-2 border-orange-100 focus:border-primary py-6 text-lg"
          data-testid="food-search-input"
        />
      </div>

      {/* Meal Plan Reminder Banner - Shows right after search for B2C users without meal plans */}
      {shouldShowMealPlanReminder && hasMealPlans === false && selectedFoods.length === 0 && !(searchQuery.length >= 2 && searchResults.length === 0) && (
        <Card 
          className="bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-2xl shadow-lg border-0"
          data-testid="meal-plan-reminder"
        >
          <div className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{t('meal_analysis.got_meal_plan')}</p>
              <p className="text-xs text-white/80">{t('meal_analysis.upload_track_better')}</p>
            </div>
            <Button
              onClick={() => navigate("/my-meal-plans")}
              size="sm"
              className="bg-white text-purple-600 hover:bg-white/90 rounded-full px-4 flex-shrink-0"
              data-testid="upload-reminder-btn"
            >
              {t('meal_plan.upload_plan')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card className="border border-orange-100 rounded-2xl overflow-hidden max-h-64 overflow-y-auto">
          {searchResults.map((food) => (
            <div
              key={food.food_id}
              onClick={() => selectFood(food)}
              className="flex items-center justify-between p-4 border-b border-orange-50 last:border-0 cursor-pointer hover:bg-orange-50/50 transition-colors"
              data-testid={`search-result-${food.food_id}`}
            >
              <div>
                <p className="font-medium text-secondary">{food.name}</p>
                <p className="text-sm text-muted-foreground">
                  {food.carbs_per_100g}g {t('meal_analysis.carbs')}/100g • GI: {food.glycemic_index}
                </p>
              </div>
              <Plus className="w-5 h-5 text-primary" />
            </div>
          ))}
        </Card>
      )}

      {/* No Results Message */}
      {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
        <Card className="border border-orange-100 rounded-2xl p-4 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            {t('meal_analysis.not_found_in_db').replace('{query}', searchQuery)}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('meal_analysis.adding_more_foods')}{" "}
            <a 
              href="mailto:support@platemind.app?subject=Food%20Suggestion&body=Hi%20PlateMind%20Team,%0A%0AI'd%20like%20to%20suggest%20adding%20the%20following%20food%20to%20your%20database:%0A%0AFood%20name:%20%0ARegion/Cuisine:%20%0A%0AThank%20you!" 
              className="text-primary hover:underline font-medium"
            >
              {t('meal_analysis.let_us_know')}
            </a>
          </p>
        </Card>
      )}

      {/* Selected Foods */}
      {selectedFoods.length > 0 && (
        <Card className="border border-orange-100 rounded-2xl p-4 space-y-3">
          <p className="font-medium text-secondary">{t('meal_analysis.selected_foods')} ({selectedFoods.length})</p>
          <div className="space-y-2">
            {selectedFoods.map((food) => (
              <div
                key={food.food_id}
                className="flex items-center justify-between p-3 bg-orange-50/50 rounded-xl"
              >
                <div>
                  <p className="font-medium text-secondary">{food.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {food.carbs_per_100g}g {t('meal_analysis.carbs')} • {food.calories_per_100g} {t('dashboard.kcal')} (per 100g)
                  </p>
                </div>
                <button
                  onClick={() => removeFood(food.food_id)}
                  className="p-1.5 rounded-full hover:bg-orange-100"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Continue Button */}
      {selectedFoods.length > 0 && (
        <Button
          onClick={proceedToConfirm}
          className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold"
          data-testid="continue-to-confirm-btn"
        >
          {t('common.continue')}
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      )}
    </div>
  );

  const renderConfirmStep = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <button
          onClick={() => setStep("search")}
          className="flex items-center gap-2 text-muted-foreground hover:text-secondary"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>
        <h1 className="font-heading text-2xl font-bold text-secondary">
          {t('meal_analysis.foods_in_meal')}
        </h1>
        <p className="text-muted-foreground">
          {t('meal_analysis.search_foods_desc')}
        </p>
      </div>

      {/* Foods List with Nutrition per 100g */}
      <div className="space-y-3">
        {selectedFoods.map((food) => (
          <Card
            key={food.food_id}
            className="border border-orange-100 rounded-2xl p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-semibold text-secondary text-lg">{food.name}</p>
                <span className="text-xs px-2 py-0.5 bg-orange-100 text-primary rounded-full">
                  {food.category || "Food"}
                </span>
              </div>
              <button
                onClick={() => removeFood(food.food_id)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Nutrition per 100g */}
            <div className="bg-orange-50/50 rounded-xl p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                {t('meal_analysis.nutrition_summary')} (per 100g)
              </p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-primary">{food.carbs_per_100g}g</p>
                  <p className="text-xs text-muted-foreground">{t('meal_analysis.carbs')}</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-secondary">{food.protein_per_100g}g</p>
                  <p className="text-xs text-muted-foreground">{t('meal_analysis.protein')}</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-secondary">{food.fat_per_100g}g</p>
                  <p className="text-xs text-muted-foreground">{t('meal_analysis.fat')}</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-secondary">{food.calories_per_100g}</p>
                  <p className="text-xs text-muted-foreground">kcal</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                {t('meal_analysis.glycemic_index')}: {food.glycemic_index}
              </p>
            </div>
          </Card>
        ))}
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => setStep("search")}
          className="flex-1 rounded-full py-5"
        >
          {t('common.edit')}
        </Button>
        <Button
          onClick={proceedToPortion}
          className="flex-1 rounded-full bg-primary text-white hover:bg-primary/90 py-5 font-bold"
          data-testid="confirm-foods-btn"
        >
          <Check className="w-4 h-4 mr-2" />
          {t('common.confirm')}
        </Button>
      </div>
    </div>
  );

  const renderPortionStep = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <button
          onClick={() => setStep("confirm")}
          className="flex items-center gap-2 text-muted-foreground hover:text-secondary"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>
        <h1 className="font-heading text-2xl font-bold text-secondary">
          {t('meal_analysis.portion_guidance')}
        </h1>
        <p className="text-muted-foreground">
          {t('meal_analysis.typical_portion')}
        </p>
      </div>

      {/* Meal Type Selection */}
      <Card className="border border-orange-100 rounded-2xl p-4">
        <p className="font-medium text-secondary mb-3">{t('glucose.reading_type')}</p>
        <Select value={mealType} onValueChange={setMealType}>
          <SelectTrigger className="rounded-xl border-orange-100">
            <SelectValue placeholder={t('glucose.reading_type')} />
          </SelectTrigger>
          <SelectContent>
            {MEAL_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label} (Target: {type.carbTarget})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      {/* Portion Selection for Each Food */}
      <div className="space-y-3">
        {selectedFoods.map((food) => (
          <Card
            key={food.food_id}
            className="border border-orange-100 rounded-2xl p-4 space-y-3"
          >
            <div className="flex items-center gap-3">
              <Scale className="w-5 h-5 text-primary" />
              <p className="font-semibold text-secondary">{food.name}</p>
            </div>
            
            {/* Portion Options */}
            <div className="grid grid-cols-4 gap-2">
              {PORTION_OPTIONS.map((option) => {
                const isSelected = option.grams === null 
                  ? ![80, 150, 250].includes(portions[food.food_id])
                  : portions[food.food_id] === option.grams;
                
                return (
                  <button
                    key={option.label}
                    onClick={() => option.grams && updatePortion(food.food_id, option.grams)}
                    className={`p-2 rounded-xl text-center transition-all ${
                      isSelected
                        ? "bg-primary text-white"
                        : "bg-orange-50 hover:bg-orange-100 text-secondary"
                    }`}
                  >
                    <p className="font-medium text-sm">{option.label}</p>
                    <p className="text-xs opacity-80">{option.description}</p>
                  </button>
                );
              })}
            </div>
            
            {/* Custom Portion Input */}
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={portions[food.food_id] || ""}
                onChange={(e) => updatePortion(food.food_id, e.target.value)}
                placeholder={t('meal_analysis.typical_portion')}
                className="rounded-xl border-orange-100"
              />
              <span className="text-muted-foreground">grams</span>
            </div>
            
            {/* Estimated Carbs Preview */}
            <div className="bg-orange-50/50 rounded-xl p-2 text-center">
              <p className="text-sm text-muted-foreground">
                {t('meal_analysis.estimated_carbs')}: <span className="font-bold text-primary">
                  {Math.round((food.carbs_per_100g * (portions[food.food_id] || 100)) / 100)}g {t('meal_analysis.carbs')}
                </span> for {portions[food.food_id] || 100}g
              </p>
            </div>
          </Card>
        ))}
      </div>

      {/* Analyze Button */}
      <Button
        onClick={analyzeWithPortions}
        disabled={loading}
        className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold"
        data-testid="analyze-meal-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            {t('meal_analysis.analyzing')}
          </>
        ) : (
          <>
            <BarChart3 className="w-5 h-5 mr-2" />
            {t('meal_analysis.analyze')}
          </>
        )}
      </Button>
    </div>
  );

  const renderResultStep = () => {
    if (!analysisResult) return null;
    
    const { 
      detected_foods, 
      meal_totals, 
      carb_classification, 
      portion_guidance,
      portion_advice,
      plate_method_tip,
      glycemic_impact,
      average_gi,
      ai_explanation 
    } = analysisResult;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="font-heading text-2xl font-bold text-secondary">
            {t('meal_analysis.analysis_result')}
          </h1>
          <p className="text-muted-foreground">
            {MEAL_TYPES.find(t => t.value === mealType)?.label || "Meal"} {t('meal_analysis.analyze')}
          </p>
        </div>

        {/* Carbohydrate Classification */}
        <Card className={`rounded-2xl p-4 ${getCarbClassColor(carb_classification?.classification)}`}>
          <div className="flex items-center gap-3">
            {carb_classification?.classification === "ideal" ? (
              <CheckCircle className="w-6 h-6" />
            ) : carb_classification?.classification === "high" ? (
              <AlertTriangle className="w-6 h-6" />
            ) : (
              <Info className="w-6 h-6" />
            )}
            <div>
              <p className="font-bold text-lg">{carb_classification?.label}</p>
              <p className="text-sm opacity-80">
                {t('meal_analysis.carb_target')}: {carb_classification?.target_range}
              </p>
            </div>
          </div>
        </Card>

        {/* Meal Totals */}
        <Card className="border border-orange-100 rounded-2xl p-4">
          <p className="font-medium text-secondary mb-3">
            {t('meal_analysis.estimated_carbs')}
          </p>
          <div className="text-center mb-4">
            <p className="text-4xl font-bold text-primary">{meal_totals?.carbs}g</p>
            <p className="text-sm text-muted-foreground">{t('meal_analysis.total_carbs')}</p>
          </div>
          
          <div className="grid grid-cols-4 gap-2 text-center border-t border-orange-100 pt-3">
            <div>
              <p className="text-lg font-bold text-secondary">{meal_totals?.protein}g</p>
              <p className="text-xs text-muted-foreground">{t('meal_analysis.protein')}</p>
            </div>
            <div>
              <p className="text-lg font-bold text-secondary">{meal_totals?.fat}g</p>
              <p className="text-xs text-muted-foreground">{t('meal_analysis.fat')}</p>
            </div>
            <div>
              <p className="text-lg font-bold text-secondary">{meal_totals?.fiber}g</p>
              <p className="text-xs text-muted-foreground">{t('meal_analysis.fiber')}</p>
            </div>
            <div>
              <p className="text-lg font-bold text-secondary">{meal_totals?.calories}</p>
              <p className="text-xs text-muted-foreground">kcal</p>
            </div>
          </div>
        </Card>

        {/* Glycemic Impact */}
        <Card className="border border-orange-100 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-secondary">{t('meal_analysis.gi_impact')}</p>
              <p className="text-sm text-muted-foreground">{t('meal_analysis.avg_gi')}: {average_gi}</p>
            </div>
            <span className={`text-xl font-bold ${getGIColor(glycemic_impact)}`}>
              {glycemic_impact}
            </span>
          </div>
        </Card>

        {/* Portion Advice (if carbs are high) */}
        {portion_advice && (
          <Card className="bg-warning/10 border border-warning/20 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-secondary">{t('meal_analysis.portion_guidance')}</p>
                <p className="text-sm text-muted-foreground mt-1">{portion_advice}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Recommended Portions */}
        {portion_guidance && portion_guidance.length > 0 && (
          <Card className="border border-orange-100 rounded-2xl p-4 space-y-3">
            <p className="font-medium text-secondary">{t('meal_analysis.portion_guidance')}</p>
            {portion_guidance.map((pg, idx) => (
              <div key={idx} className="bg-orange-50/50 rounded-xl p-3">
                <p className="font-medium text-secondary">{pg.food_name}</p>
                <p className="text-sm text-muted-foreground">
                  {pg.recommended_portion_g} • {pg.estimated_carbs} {t('meal_analysis.carbs')}
                </p>
              </div>
            ))}
          </Card>
        )}

        {/* Diabetes Plate Method Tip */}
        <Card className="bg-primary/5 border border-primary/10 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-secondary">{t('meal_analysis.diabetes_plate_method')}</p>
              <p className="text-sm text-muted-foreground mt-1">{plate_method_tip}</p>
            </div>
          </div>
        </Card>

        {/* Lana's Insight */}
        {ai_explanation && (
          <Card className="border border-orange-100 rounded-2xl p-4">
            <p className="font-medium text-secondary mb-2">{t('meal_analysis.lana_insight')}</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {ai_explanation}
            </p>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={resetAnalysis}
            className="flex-1 rounded-full py-5"
          >
            {t('meal_analysis.analyze_another')}
          </Button>
          <Link to="/dashboard" className="flex-1">
            <Button className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-5">
              {t('common.done')}
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-28">
      {/* Header */}
      <header className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-orange-100 px-4 py-3 z-40">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Link to="/dashboard" className="p-2 -ml-2">
            <ArrowLeft className="w-5 h-5 text-secondary" />
          </Link>
          <div>
            <h1 className="font-heading font-bold text-secondary">{t('meal_analysis.title')}</h1>
            <p className="text-xs text-muted-foreground">
              {step === "search" && t('meal_analysis.step_1_select')}
              {step === "confirm" && t('meal_analysis.step_2_confirm')}
              {step === "portion" && t('meal_analysis.step_3_portions')}
              {step === "result" && t('meal_analysis.analysis_complete')}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        {renderContent()}
      </main>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Paywall Modal */}
      <Paywall
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        trigger="meal_limit"
        usageData={usageData}
      />
    </div>
  );
};

export default EnhancedMealAnalysis;
